import {ActionTree} from "vuex";
import {RootState} from "@/store/types";
import http from "@/http";
import router from "@/router";
import { Actions, StartResetPassword, State } from '@/store/user/types';
import {isDevHost} from "@/utils/host";
import { AxiosError } from 'axios';
import { ErrorResponseData, ServiceNumbers } from '@/types';
import { getAuthErrorMessage } from '@/utils/main';

const actions: ActionTree<State, RootState> & Actions = {
    async login({commit}, user) {
        try {
            localStorage.removeItem('logout')
            const { data } = await http.post('/api/auth/login', user)
            commit('setToken', data.token)
            localStorage.setItem('token', data.token)
            localStorage.removeItem('pharmId')
            localStorage.removeItem('pharmSkip')
            localStorage.removeItem('pharmSearch')
            localStorage.removeItem("activeNextMonth")
            localStorage.removeItem("activePrevMonth")
            localStorage.removeItem("sidebarCollapsed")
            router?.push(`/`)
        } catch (e) {
            commit('setError', getAuthErrorMessage(e))
            localStorage.removeItem('token')
        }
    },
    async loginTest({commit}, user) {
        try {
            localStorage.removeItem('logout')
            const {data} = await http.post('/api/auth/login-test', user)
            commit('setToken', data.token)
            localStorage.setItem('token', data.token)
            localStorage.removeItem('pharmId')
            localStorage.removeItem('pharmSkip')
            localStorage.removeItem('pharmSearch')
            localStorage.removeItem("activeNextMonth")
            localStorage.removeItem("activePrevMonth")
            router?.push(`/`)
        } catch (e) {
            commit('setError', getAuthErrorMessage(e))
            localStorage.removeItem('token')
        }
    },
    async logout({commit}) {
        try {
            await http.post('/api/auth/logout')
            localStorage.removeItem('token')
            localStorage.removeItem('pharmId')
            localStorage.removeItem('pharmSkip')
            localStorage.removeItem('pharmSearch')
            localStorage.removeItem("activeNextMonth")
            localStorage.removeItem("activePrevMonth")
            commit('setToken', '')
        } catch (e) {
            commit('setError', e)
        } finally {
            localStorage.removeItem('pharmId')
            localStorage.removeItem('token')
            commit('setToken', '')
            commit('setError', '')
            isDevHost() ? router?.push(`/authentication-test`) : router?.push(`/authentication`)
        }
    },
    async userInfo({rootState, commit}) {
        try {
            // Получаем информацию о пользователе
            const response = await http.get('/api/user/profile')
            try {
                const { data } = response;
                commit('setUser', data);

                // Проверяем роли у пользователя и добавляем для проверок в rootState
                const permittedRoles = ['OFFLINE_ADMIN', 'GURV', 'OFFLINE_DIRECTOR', 'WFM_READER'] // Общие роли для доступа к веб-графику
                const pharmsHeadsRole = ['ADMIN_ZORT', 'DEPUTY_DIRECTOR'] // Роль руководителя аптек
                const beruExtendedRoles = ['EXTENDED_ZORT', 'REVISION', 'STOP_TRAIDING'] // Роль пользователя с расширенными правами

                const isRoleHasAccess = data.roles.some((item:string) => permittedRoles.includes(item))
                rootState.isOffice = data.serviceNumbers.some((item: ServiceNumbers) => item.isOffice)
                rootState.gurvRole = data.roles.some((item:string) => item === 'GURV')
                rootState.offlineDirectorRole = data.roles.some((item:string) => item === 'OFFLINE_DIRECTOR')
                rootState.offlineAdminRole = data.roles.some((item:string) => item === 'OFFLINE_ADMIN')
                
                // Роли для доступа на страницу Беру и определения запроса для списка аптек
                rootState.beruAccessRole = data.roles.some((item:string) => beruExtendedRoles.includes(item)) || data.roles.some((item:string) => pharmsHeadsRole.includes(item))
                rootState.pharmsHeadRole = data.roles.some((item:string) => pharmsHeadsRole.includes(item))
                rootState.extendedRole = data.roles.some((item:string) => beruExtendedRoles.includes(item))

                // Пользователь с ролью только для чтения (игнорируется если есть роли GURV или OFFLINE_ADMIN)
                rootState.wfmReaderRole = data.roles.some((item:string) => item === 'WFM_READER')

                const isSettlementPosition = data.roles.some(item => item.settlementPosition !== null)
                // Доступа нет - выходим из аккаунта, очищаем localStorage и возвращаем на страницу авторизации
                if (!(isRoleHasAccess && isSettlementPosition)) {
                    await http.post('/api/auth/logout')
                    localStorage.removeItem('pharmId')
                    localStorage.removeItem('token')
                    isDevHost() ? router?.push(`/authentication-test`) : router?.push(`/authentication`)
                    commit('setError', 'У вас отсутствует доступ к администрированию графиков смен')
                    rootState.axiosController.abort()
                }
            } catch (responseError) {
                console.error(responseError);
            }

        } catch (e) {
            const error = e as AxiosError<ErrorResponseData>

            if (error.code === "ERR_NETWORK" || error.response?.status === 401) {
                localStorage.removeItem('pharmId')
                localStorage.removeItem('token')
                isDevHost() ? router?.push(`/authentication-test`) : router?.push(`/authentication`)
                rootState.axiosController.abort()
            }

            if (localStorage.getItem('token') && error.response?.status === 503) {
                localStorage.removeItem('pharmId')
                localStorage.removeItem('token')
                isDevHost() ? router?.push(`/authentication-test`) : router?.push(`/authentication`)
                commit('setError', getAuthErrorMessage(e))
                rootState.axiosController.abort()
            }

            // Дополнительная проверка при ошибке профиля
            if (router.currentRoute.value.fullPath === '/' && error.response?.status === 500) {
                localStorage.removeItem('pharmId')
                localStorage.removeItem('token')
                isDevHost() ? router?.push(`/authentication-test`) : router?.push(`/authentication`)
                rootState.axiosController.abort()
            }
        }
    },
    async startReset({commit}, payload: StartResetPassword) {
        try {
            const data = await http.post('/api/user-reset-pass/start-reset', payload)
            sessionStorage.setItem('resetSessionUID', data.data.sessionUid)
            commit('setPasswordResetStep', 2)
        } catch (e) {
            const error = e as AxiosError<ErrorResponseData>
            commit('setError', error.response?.data.message)
        }
    },
    async phoneInput({commit}, phone: string) {
        try {
            let sessionUid = sessionStorage.getItem('resetSessionUID')
            if (sessionStorage.getItem('resetSessionUID') && sessionStorage.getItem('resetCodeSessionUID')) {
                sessionUid = sessionStorage.getItem('resetCodeSessionUID')
            }
            const data = await http.post('/api/user-reset-pass/phone-input', {sessionUid, phone})
            if (!data || data.status !== 201) return
            sessionStorage.setItem('resetCodeSessionUID', data.data.sessionUid)
            commit('setPasswordResetStep', 3)
        } catch (e) {
            const error = e as AxiosError<ErrorResponseData>
            commit('setError', error.response?.data.message)
        }
    },
    async confirmCode({commit}, code: string) {
        try {
            const codeSessionUid = sessionStorage.getItem('resetCodeSessionUID')
            const data = await http.post('/api/user-reset-pass/confirm-code', {sessionUid: codeSessionUid, code})
            if (!data || data.status !== 201) return
            sessionStorage.setItem('resetPassSessionUID', data.data.sessionUid)
            commit('setPasswordResetStep', 4)
        } catch (e) {
            const error = e as AxiosError<ErrorResponseData>
            commit('setError', error.response?.data.message)
        }
    },
    async resetPass({ commit }, { newPassword, type }) {
        try {
            const payload: { newPassword: string; sessionUid?: string } = { newPassword };

            if (type === 'force') {
                const resetPassSessionUID = sessionStorage.getItem('resetPassSessionUID');
                if (!resetPassSessionUID) {
                    return;
                }
                payload.sessionUid = resetPassSessionUID;
            }

            const url = type === 'force' ? '/api/user-reset-pass/force-reset-pass' : '/api/user-reset-pass/soft-reset-pass';
            const data = await http.post(url, payload);

            if (!data || data.status !== 201) return;
            commit('setPasswordResetStep', 5);
        } catch (e) {
            const error = e as AxiosError<ErrorResponseData>;
            commit('setError', error.response?.data.message);
        }
    },
    updatePasswordResetStep({ commit }, step) {
        commit('setPasswordResetStep', step)
    }
};

export default actions